import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Role } from '../services/usersService';
import { departmentsApi, DepartmentDto } from '../services/settingsService';
import MultiSelectChips from './common/MultiSelectChips';
import PasswordInput from './common/PasswordInput';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: var(--surface-color);
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.span`
  color: var(--success-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const HelperText = styled.span`
  color: var(--text-secondary);
  font-size: 0.78rem;
  margin-top: 0.25rem;
  font-style: italic;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
  gap: 0.5rem;

  input { margin: 0; }
  input:disabled { cursor: not-allowed; }
`;

const InfoNote = styled.div`
  background: #f0f7ff;
  border: 1px solid #c2dbff;
  color: var(--text-secondary);
  padding: 0.6rem 0.85rem;
  border-radius: var(--border-radius-md);
  font-size: 0.82rem;
  line-height: 1.4;
`;

const GeneralError = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--error-color);
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  ${props => props.$variant === 'primary' ? `
    background-color: var(--primary-color);
    color: white;
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }
  ` : `
    background-color: transparent;
    color: var(--secondary-color);
    border: 1px solid var(--secondary-color);
    
    &:hover:not(:disabled) {
      background-color: var(--hover-background);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export interface User {
  id?: number;
  fullName: string;
  email: string;
  phone: string;
  role: Role;
  canViewClientFinance: boolean;
  canViewFinancialReports: boolean;
  departmentIds: number[];
  isActive: boolean;
  // P8a: משתמש המקושר לרשומה בספריית הסוכנים/ארגונים — שמו מנוהל שם ולכן read-only כאן.
  isDirectoryLinked?: boolean;
}

interface UserFormProps {
  user?: User;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (user: User, password?: string) => Promise<void>;
  isLoading?: boolean;
}

const emptyForm = (): User & { password?: string } => ({
  fullName: '',
  email: '',
  phone: '',
  role: Role.Employee,
  canViewClientFinance: false,
  canViewFinancialReports: false,
  departmentIds: [],
  isActive: true,
  password: '',
});

const UserForm: React.FC<UserFormProps> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<User & { password?: string }>(emptyForm());
  // P8a: שם של סוכן מקושר מנוהל בספריית הסוכנים/ארגונים ולכן נעול לעריכה כאן.
  const isNameLocked = !!user?.isDirectoryLinked;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        role: user.role,
        canViewClientFinance: user.canViewClientFinance,
        canViewFinancialReports: user.canViewFinancialReports,
        departmentIds: [...(user.departmentIds ?? [])],
        isActive: user.isActive,
        password: ''
      });
    } else {
      setFormData(emptyForm());
    }
    setErrors({});
    setGeneralError('');
  }, [user, isOpen]);

  // טעינת מחלקות לבורר (בעת פתיחת הטופס)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    departmentsApi.getAll()
      .then(data => { if (!cancelled) setDepartments(data); })
      .catch(() => { if (!cancelled) setDepartments([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // אפשרויות המחלקות: פעילות בלבד + מחלקה משויכת-כבר שהושבתה (כדי שלא "תיעלם" בעריכה)
  const departmentOptions = useMemo(() => {
    const map = new Map<number, string>();
    departments.filter(d => d.isActive).forEach(d => map.set(d.id, d.name));
    departments.filter(d => formData.departmentIds.includes(d.id)).forEach(d => map.set(d.id, d.name));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [departments, formData.departmentIds]);

  // מיתוג ה-invariants בעת שינוי תפקיד (השרת הוא הסמכות; זה רק מונע קלט בלתי-אפשרי)
  const handleRoleChange = (role: Role) => {
    setFormData(prev => {
      if (role === Role.Manager) {
        return { ...prev, role, canViewClientFinance: true };
      }
      if (role === Role.Agent) {
        return { ...prev, role, canViewClientFinance: false, canViewFinancialReports: false };
      }
      // Employee — דוחות פיננסיים לא מוצעים
      return { ...prev, role, canViewFinancialReports: false };
    });
    if (errors.role) setErrors(prev => ({ ...prev, role: '' }));
  };

  // Validate a single field (for onBlur validation)
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'fullName':
        if (!value || !value.trim()) {
          return 'שם מלא הוא שדה חובה';
        }
        break;

      case 'email':
        if (!value || !value.trim()) {
          return 'דוא"ל הוא שדה חובה';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          return 'פורמט הדוא"ל אינו תקין';
        }
        break;

      case 'phone':
        if (!value || !value.trim()) {
          return 'טלפון הוא שדה חובה';
        } else if (value.trim().length < 9) {
          return 'מספר טלפון חייב להכיל לפחות 9 ספרות';
        }
        break;

      case 'password':
        if (!user && (!value || value.length < 6)) {
          return 'סיסמה חייבת להכיל לפחות 6 תווים';
        }
        break;

      default:
        return '';
    }
    return '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const fullNameError = validateField('fullName', formData.fullName);
    if (fullNameError) newErrors.fullName = fullNameError;

    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validateField('phone', formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (!user) {
      const passwordError = validateField('password', formData.password);
      if (passwordError) newErrors.password = passwordError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setGeneralError(''); // Clear previous errors
      await onSubmit(formData, formData.password);
      // Don't call onClose() here - let the parent component close it after successful API call
    } catch (error: any) {
      console.error('Error submitting form:', error);

      // Extract error message from server response
      let errorMessage = 'שגיאה בשמירת הנתונים';

      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages: string[] = [];
        Object.values(validationErrors).forEach((value: any) => {
          if (Array.isArray(value)) {
            errorMessages.push(...value);
          } else {
            errorMessages.push(String(value));
          }
        });
        errorMessage = errorMessages.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setGeneralError(errorMessage);
    }
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      // Clear error if field is now valid
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isFieldValid = (field: string, value: any): boolean => {
    return !validateField(field, value) && value && value.toString().trim() !== '';
  };

  if (!isOpen) return null;

  return (
    <Overlay>
      <Modal>
        <ModalHeader>
          <ModalTitle>{user ? 'עריכת משתמש' : 'הוספת משתמש חדש'}</ModalTitle>
          <CloseButton onClick={onClose} type="button">×</CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          {generalError && (
            <GeneralError>{generalError}</GeneralError>
          )}

          <FormGroup>
            <Label>שם מלא *</Label>
            <Input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              onBlur={(e) => handleBlur('fullName', e.target.value)}
              className={errors.fullName ? 'error' : ''}
              disabled={isLoading || isNameLocked}
            />
            {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
            {!errors.fullName && !isNameLocked && isFieldValid('fullName', formData.fullName) && (
              <SuccessMessage>✓ תקין</SuccessMessage>
            )}
            {isNameLocked && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                השם מנוהל בספריית הסוכנים והארגונים ולכן אינו ניתן לעריכה כאן.
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>דוא"ל *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={(e) => handleBlur('email', e.target.value)}
              className={errors.email ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            {!errors.email && isFieldValid('email', formData.email) && (
              <SuccessMessage>✓ כתובת דוא"ל תקינה</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>טלפון *</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={(e) => handleBlur('phone', e.target.value)}
              className={errors.phone ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            {!errors.phone && isFieldValid('phone', formData.phone) && (
              <SuccessMessage>✓ מספר טלפון תקין</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>תפקיד *</Label>
            <Select
              value={formData.role}
              onChange={(e) => handleRoleChange(parseInt(e.target.value) as Role)}
              disabled={isLoading}
            >
              <option value={Role.Manager}>מנהל</option>
              <option value={Role.Employee}>עובד</option>
              <option value={Role.Agent}>סוכן</option>
            </Select>
          </FormGroup>

          {/* דגלי פיננסים — עם מיתוג ה-invariants (השרת הוא הסמכות) */}
          <FormGroup>
            <Label>הרשאות פיננסים</Label>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.role === Role.Manager ? true : formData.canViewClientFinance}
                onChange={(e) => handleInputChange('canViewClientFinance', e.target.checked)}
                disabled={isLoading || formData.role === Role.Manager || formData.role === Role.Agent}
              />
              צפייה בפיננסים של לקוח
            </CheckboxLabel>
            {formData.role === Role.Manager && (
              <HelperText>מנהל רואה תמיד את הפיננסים של לקוח — לא ניתן לכבות.</HelperText>
            )}
            {formData.role === Role.Manager && (
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.canViewFinancialReports}
                  onChange={(e) => handleInputChange('canViewFinancialReports', e.target.checked)}
                  disabled={isLoading}
                />
                צפייה בדוחות פיננסיים
              </CheckboxLabel>
            )}
          </FormGroup>

          {/* שיוך מחלקות (רבים-לרבים) */}
          <FormGroup>
            <Label>מחלקות</Label>
            <MultiSelectChips
              options={departmentOptions}
              selectedIds={formData.departmentIds}
              onChange={(ids) => setFormData(prev => ({ ...prev, departmentIds: ids }))}
              disabled={isLoading}
              placeholder="+ הוסף מחלקה"
              emptyText="ללא שיוך למחלקה"
            />
          </FormGroup>

          <InfoNote>
            שינוי תפקיד, דגלי פיננסים או מחלקות ייכנס לתוקף בכניסה הבאה של המשתמש למערכת.
          </InfoNote>

          {!user && (
            <FormGroup>
              <Label>סיסמה *</Label>
              <PasswordInput
                inputComponent={Input}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onBlur={(e) => handleBlur('password', e.target.value)}
                className={errors.password ? 'error' : ''}
                disabled={isLoading}
                placeholder="הזן סיסמה (לפחות 6 תווים)"
              />
              {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
              {!errors.password && formData.password && formData.password.length >= 6 && (
                <SuccessMessage>✓ סיסמה תקינה</SuccessMessage>
              )}
            </FormGroup>
          )}

          {user && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  disabled={isLoading}
                  style={{ marginLeft: '0.5rem' }}
                />
                משתמש פעיל
              </Label>
            </FormGroup>
          )}

          <ButtonGroup>
            <Button type="button" onClick={onClose} disabled={isLoading}>
              ביטול
            </Button>
            <Button type="submit" $variant="primary" disabled={isLoading}>
              {isLoading ? 'שומר...' : user ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default UserForm;